"use client"

import React from 'react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { Lock } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { confirmPasswordReset } from '@/services/user'
import NoChrome from '@/components/NoChrome'

export default function ResetPasswordPage() {
  const router = useRouter()
  const params = useSearchParams()
  const token = params.get('token') || ''
  const [password, setPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)

  const disabled = !token || !password || !confirmPassword || submitting

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    setSubmitting(true)
    try {
      await confirmPasswordReset({
        token,
        password,
        confirm_password: confirmPassword,
      })
      toast.success('Password updated')
      router.push('/login')
    } catch (err: any) {
      const data = err?.response?.data
      const detail = data?.token || data?.password || data?.confirm_password || data?.detail || 'Unable to reset password'
      toast.error(String(detail))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <NoChrome>
      <main className="flex min-h-screen items-center justify-center bg-[#F7F8FC] px-4">
        <section className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-center gap-2">
          <Lock className="h-5 w-5 text-slate-700" />
          <div>
            <h1 className="text-xl font-semibold text-slate-950">Reset password</h1>
            <p className="mt-1 text-sm text-slate-600">Enter and confirm your new password.</p>
          </div>
        </div>

        {!token ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            This password reset link is missing a token.
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="mt-5 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-900" htmlFor="new-password">New password</label>
            <Input
              id="new-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-900" htmlFor="confirm-new-password">Confirm new password</label>
            <Input
              id="confirm-new-password"
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              required
            />
          </div>

          <Button type="submit" className="w-full rounded-md" disabled={disabled}>
            {submitting ? 'Updating...' : 'Update password'}
          </Button>
        </form>

        <p className="mt-6 text-sm text-slate-600">
          Remembered your password?{' '}
          <Link href="/login" className="font-medium text-primary underline-offset-4 hover:underline">
            Go to sign in
          </Link>
        </p>
        </section>
      </main>
    </NoChrome>
  )
}
